import { ClientType } from '@/shared/types/enum';
import { ModelNamePick } from '@/shared/types/model';
import { merge } from '@/shared/utils/object';

import { Account, IndividualView, OrganisationView, Service, TaskView, User } from '@prisma/client';

export type PlaceholderPrefix = 'Account' | 'User' | 'Organisation' | 'Individual' | 'Service' | 'Task';

export type PlaceholderModel = Account | User | OrganisationView | IndividualView | Service | TaskView;

export type PlaceholderModelName = ModelNamePick<
  'Account' | 'User' | 'OrganisationView' | 'IndividualView' | 'Service' | 'Task'
>;

export const PlaceholderPrefixModelMap: Record<PlaceholderPrefix, PlaceholderModelName> = {
  Account: 'Account',
  User: 'User',
  Organisation: 'OrganisationView',
  Individual: 'IndividualView',
  Service: 'Service',
  Task: 'Task',
};

export const PlaceholderDateFormats = {
  DayMonthYear: 'dd/MM/yy',
  Day: 'd',
  DayOrdinal: 'do',
  DayName: 'EEEE',
  DayNameShort: 'EEE',
  Month: 'M',
  MonthName: 'MMMM',
  MonthNameShort: 'MMM',
  Year: 'yyyy',
};

const PlaceholderDateFormatType: keyof typeof PlaceholderDateFormats = 'DayMonthYear';

type Placeholder<T> = {
  name: string;
  column: keyof T & string;
  dateFormat?: string;
};

export const PLACEHOLDER_DELIMITERS: [string, string] = ['{{', '}}'];

export const PLACEHOLDER_REGEX = new RegExp(
  `(${PLACEHOLDER_DELIMITERS[0]}(${Object.keys(PlaceholderPrefixModelMap).join('|')})\\.([^}]+)${PLACEHOLDER_DELIMITERS[1]})`,
  'g',
);

export const PLACEHOLDER_RTE_REGEX = new RegExp(`<span[^>]*>${PLACEHOLDER_REGEX.source}</span>`, 'g');

const prefix = <T>(prefix: PlaceholderPrefix, placeholders: Placeholder<T>[]) => {
  return placeholders.map((placeholder) => ({ ...placeholder, name: `${prefix}.${placeholder.name}` }));
};

const datePlaceholders = <T>(base: Placeholder<T>) => [
  ...Object.keys(PlaceholderDateFormats).map((dateFormatType) => ({
    name: [base.name, ...(dateFormatType !== PlaceholderDateFormatType ? [dateFormatType] : [])].join('.'),
    column: base.column,
    dateFormat: PlaceholderDateFormats[dateFormatType as keyof typeof PlaceholderDateFormats],
  })),
];

const customFieldPlaceholders = [
  { name: 'CustomField1', column: 'customField1' },
  { name: 'CustomField2', column: 'customField2' },
  { name: 'CustomField3', column: 'customField3' },
  { name: 'CustomField4', column: 'customField4' },
  { name: 'CustomField5', column: 'customField5' },
  { name: 'CustomField6', column: 'customField6' },
  { name: 'CustomField7', column: 'customField7' },
  { name: 'CustomField8', column: 'customField8' },
  { name: 'CustomField9', column: 'customField9' },
  { name: 'CustomField10', column: 'customField10' },
] as const;

const AccountPlaceholders: Placeholder<Account>[] = prefix('Account', [
  { name: 'Name', column: 'name' },
  { name: 'FromName', column: 'emailFromName' },
  { name: 'FromEmail', column: 'emailFromEmail' },
]);

const UserPlaceholders: Placeholder<User>[] = prefix('User', [
  { name: 'FirstName', column: 'firstName' },
  { name: 'LastName', column: 'lastName' },
  { name: 'Email', column: 'email' },
]);

const OrganisationPlaceholders: Placeholder<OrganisationView>[] = prefix('Organisation', [
  { name: 'Name', column: 'name' },
  { name: 'Type', column: 'organisationType' },
  { name: 'RegistrationNumber', column: 'registrationNumber' },
  ...datePlaceholders<OrganisationView>({ name: 'IncorporationDate', column: 'incorporationDate' }),
  // { name: 'YearEnd', column: 'yearEndDate' } ...datePlaceholders<>(),
  { name: 'AuthenticationCode', column: 'companiesHouseAuthenticationCode' },
  { name: 'UTR', column: 'utr' },
  { name: 'Reference', column: 'internalReference' },
  ...datePlaceholders<OrganisationView>({ name: 'EngagementSigned', column: 'engagementSignedDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'AMLLastChecked', column: 'amlLastCheckedDate' }),
  // { name: 'AddressLine1', column: 'postalAddressLine_1' },
  // { name: 'AddressLine2', column: 'postalAddressLine_2' },
  // { name: 'AddressTown', column: 'postalAddressTown' },
  // { name: 'AddressCounty', column: 'postalAddressCounty' },
  // { name: 'AddressPostcode', column: 'postalAddressPostcode' },
  { name: 'Phone1', column: 'mainTelephoneNumber' },
  { name: 'Phone2', column: 'secondaryTelephoneNumber' },
  { name: 'Phone', column: 'mainTelephoneNumber' },
  { name: 'Email1', column: 'mainEmail' },
  { name: 'Email2', column: 'secondaryEmail' },
  { name: 'Email', column: 'mainEmail' },
  { name: 'Website', column: 'website' },
  { name: 'PrimaryContactFirstName', column: 'principalContactFirstName' },
  { name: 'PrimaryContactLastName', column: 'principalContactLastName' },
  { name: 'PrimaryContactEmail', column: 'principalContactEmail' },
  { name: 'PrimaryContactPhone', column: 'contactTelephoneNumber' },
  ...datePlaceholders<OrganisationView>({ name: 'AccountsLastMadeUpTo', column: 'accountsLastMadeUpToDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'AccountsPeriodEnd', column: 'accountsPeriodEndDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'AccountsDue', column: 'accountsDueDate' }),
  ...datePlaceholders<OrganisationView>({
    name: 'ConfirmationStatementLastMadeUpTo',
    column: 'confirmationStatementLastMadeUpToDate',
  }),
  ...datePlaceholders<OrganisationView>({
    name: 'ConfirmationStatementNextMadeUpTo',
    column: 'confirmationStatementNextMadeUpToDate',
  }),
  ...datePlaceholders<OrganisationView>({ name: 'ConfirmationStatementDue', column: 'confirmationStatementDueDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'TaxReturnLastMadeUpTo', column: 'taxReturnLastMadeUpToDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'TaxPeriodEnd', column: 'taxPeriodEndDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'TaxReturnDue', column: 'taxReturnDueDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'TaxPaymentLastMadeUpTo', column: 'taxPaymentLastMadeUpToDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'TaxPaymentDue', column: 'taxPaymentDueDate' }),
  { name: 'VATNumber', column: 'vatHmrcRegistrationNumber' },
  ...datePlaceholders<OrganisationView>({ name: 'VATRegistrationDate', column: 'vatRegistrationDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'VATDeregistrationDate', column: 'vatDeregistrationDate' }),
  // { name: 'VATQuarters', column: 'vatQuarters' },
  ...datePlaceholders<OrganisationView>({ name: 'VATLastMadeUpTo', column: 'vatLastMadeUpToDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'VATPeriodEnd', column: 'vatPeriodEndDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'VATReturnDue', column: 'vatReturnDueDate' }),
  { name: 'PAYEReference', column: 'payeReference' },
  { name: 'PAYEAccountsOfficeReference', column: 'payeAccountsOfficeReferenceNumber' },
  // { name: 'PayrollFrequency', column: 'payrollFrequency' },
  // { name: 'PayrollDay', column: 'payrollDay' },
  ...datePlaceholders<OrganisationView>({ name: 'PayrollLastMadeUpTo', column: 'payrollLastMadeUpToDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'PayrollDue', column: 'payrollDueDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'PayrollPaymentDue', column: 'payrollPaymentDueDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'CISLastMadeUpTo', column: 'cisLastMadeUpToDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'CISPeriodEnd', column: 'cisPeriodEndDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'CISReturnDue', column: 'cisReturnDueDate' }),
  // { name: 'BookkeepingFrequency', column: 'bookkeepingFrequency' },
  // { name: 'BookkeepingDay', column: 'bookkeepingDay' },
  ...datePlaceholders<OrganisationView>({ name: 'BookkeepingLastMadeUpTo', column: 'bookkeepingLastMadeUpToDate' }),
  ...datePlaceholders<OrganisationView>({ name: 'BookkeepingDue', column: 'bookkeepingDueDate' }),
  ...customFieldPlaceholders,
]);

const IndividualPlaceholders: Placeholder<IndividualView>[] = prefix('Individual', [
  { name: 'FirstName', column: 'givenName' },
  { name: 'LastName', column: 'surname' },
  { name: 'FullName', column: 'name' },
  { name: 'UTR', column: 'utr' },
  { name: 'NINumber', column: 'nationalInsuranceNumber' },
  { name: 'Phone1', column: 'phone1' },
  { name: 'Phone2', column: 'phone2' },
  { name: 'Phone', column: 'phone1' },
  { name: 'Email', column: 'emailAddress' },
  // { name: 'Address', column: 'address' },
  // { name: 'Postcode', column: 'postcode' },
  // { name: 'Reference', column: 'internalReference' },
  ...datePlaceholders<IndividualView>({ name: 'EngagementSigned', column: 'engagementSignedDate' }),
  ...datePlaceholders<IndividualView>({ name: 'AMLLastChecked', column: 'amlLastCheckedDate' }),
  ...datePlaceholders<IndividualView>({ name: 'TaxReturnLastMadeUpTo', column: 'taxReturnLastMadeUpToDate' }),
  ...datePlaceholders<IndividualView>({ name: 'TaxPeriodEnd', column: 'taxReturnPeriodEndDate' }),
  ...datePlaceholders<IndividualView>({ name: 'TaxReturnDue', column: 'taxReturnDueDate' }),
  ...customFieldPlaceholders,
]);

const ServicePlaceholders: Placeholder<Service>[] = prefix('Service', [{ name: 'Name', column: 'name' }]);

const TaskPlaceholders: Placeholder<TaskView & { portalUrl?: string }>[] = prefix('Task', [
  { name: 'Name', column: 'task' },
  { name: 'PortalURL', column: 'portalUrl' },
  ...datePlaceholders<TaskView>({ name: 'Due', column: 'deadlineDate' }),
  ...datePlaceholders<TaskView>({ name: 'PlannedStart', column: 'plannedStartDate' }),
  ...datePlaceholders<TaskView>({ name: 'PlannedCompletion', column: 'plannedCompletionDate' }),
  ...datePlaceholders<TaskView>({ name: 'Started', column: 'startedDate' }),
  ...datePlaceholders<TaskView>({ name: 'Completed', column: 'completedDate' }),
  ...customFieldPlaceholders,
  { name: 'Period', column: 'smartTaskPeriodName' },
]);

const Placeholders: Placeholder<any>[] = [
  ...AccountPlaceholders,
  ...UserPlaceholders,
  ...OrganisationPlaceholders,
  ...IndividualPlaceholders,
  ...ServicePlaceholders,
  ...TaskPlaceholders,
];

export const PlaceholderLookup: Record<string, Placeholder<any> | undefined> = Placeholders.map((placeholder) => ({
  [placeholder.name]: placeholder,
})).reduce(merge);

export const placeholdersForPrefixes = (prefixes: PlaceholderPrefix[], clientType?: ClientType) => {
  return Placeholders.filter(
    (placeholder) =>
      prefixes.some((prefix) => placeholder.name.startsWith(`${prefix}.`)) &&
      (clientType === undefined ||
        (clientType === ClientType.Organisation && !placeholder.name.startsWith('Individual.')) ||
        (clientType === ClientType.Individual && !placeholder.name.startsWith('Organisation.'))),
  );
};

export const templateContentToRte = (content: string) =>
  content
    ? content.replaceAll(PLACEHOLDER_REGEX, '<span contenteditable="false" class="e-mention-chip">$1</span>')
    : content;

export const templateContentFromRte = (content: string) =>
  content ? content.replaceAll(PLACEHOLDER_RTE_REGEX, '$1') : content;
